import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { useCan, Privilege } from '@feedflo/access-control';
import { getFaultName } from '@feedflo/fault-codes';

// Our Own imports (absolute)
import CollapsibleSection from '../../../atoms/CollapsibleSection';
import FeedFloToolTip from '../../../atoms/FeedFloToolTip/FeedFloToolTip';
import { InfoIconSVG, PencilIconSVG } from '../../../atoms/Icons';
import Heading from '../../../atoms/Heading';
import LoadingSkeleton from '../../../atoms/LoadingSkeleton';
import StatusBadge from '../../../atoms/StatusBadge/StatusBadge';
import TransportMethodSelector from '../../../molecules/TransportMethodSelector/TransportMethodSelector';
import MessageHistoryLink from '../../../molecules/MessageHistoryLink';
import {
  BARN_DIFF_REASON,
  getBarnPolicyDifference,
  groupBarnSettingsByBarnAndTier,
  groupSettingsBy,
} from '../../../utils/notificationPolicyDiff';
import {
  formatAlertThresholdSummary,
  formatReminderSummary,
  formatResolvedSummary,
} from '../../../utils/tierNotificationSettings';
import useUser from '../../../utils/hooks/useUser';

// Relative path imports (./)
import {
  GET_USER_TIER_ASSIGNMENTS_GQL,
  GET_ORG_TIER_SETTINGS_ALL_GQL,
  GET_BARN_TIER_SETTINGS_GQL,
} from './queries';
import './UserNotificationSettings.scss';

const POLICY_CHANGE_TOOLTIP =
  'Contact your organization administrator to change your notification policy.';

function PolicyEditAffordance({ canEdit, editTo, editLabel }) {
  if (canEdit) {
    return (
      <Link
        to={editTo}
        className="UserNotificationSettings-editLink"
        aria-label={editLabel}
        title={editLabel}
      >
        <PencilIconSVG className="UserNotificationSettings-editIcon" />
      </Link>
    );
  }

  return (
    <FeedFloToolTip description={POLICY_CHANGE_TOOLTIP} multiline position="top">
      <span className="UserNotificationSettings-infoTrigger" tabIndex={0} aria-label={POLICY_CHANGE_TOOLTIP}>
        <InfoIconSVG className="UserNotificationSettings-infoIcon" />
      </span>
    </FeedFloToolTip>
  );
}

PolicyEditAffordance.propTypes = {
  canEdit: PropTypes.bool.isRequired,
  editTo: PropTypes.string.isRequired,
  editLabel: PropTypes.string.isRequired,
};

function TierSettingsSummaryView({ settings, headerText }) {
  if (!settings || settings.length === 0) {
    return null;
  }

  const enabledSettings = settings.filter((s) => s.enabled);

  if (enabledSettings.length === 0) {
    return (
      <div className="UserNotificationSettings-tierSettingsSummary">
        <div className="UserNotificationSettings-tierSettingsText">No alerts enabled</div>
      </div>
    );
  }

  return (
    <div className="UserNotificationSettings-tierSettingsSummary">
      {headerText && (
        <div className="UserNotificationSettings-tierSettingsHeader">{headerText}</div>
      )}
      <div className="UserNotificationSettings-tierSettingsText">
        {enabledSettings.map((setting) => {
          const faultName = getFaultName(setting.code.toString());
          const faultCode = parseInt(setting.code, 10);
          const alertText = formatAlertThresholdSummary({
            faultCode,
            thresholdSeconds: setting.alert_threshold_seconds,
            thresholdType: setting.alert_threshold_type,
          });
          const reminderText =
            setting.renotify_enabled && setting.renotify_threshold_seconds > 0
              ? formatReminderSummary(setting.renotify_threshold_seconds / 3600)
              : '';
          const resolvedText = setting.notify_on_resolved ? formatResolvedSummary(faultCode) : '';

          return (
            <div key={setting.code} className="UserNotificationSettings-tierSettingItem">
              <strong>{faultName}</strong>: {alertText}
              {reminderText}
              {resolvedText}
            </div>
          );
        })}
      </div>
    </div>
  );
}

TierSettingsSummaryView.propTypes = {
  settings: PropTypes.array,
  headerText: PropTypes.string,
};

function BarnPolicyReasonBadges({ reasons }) {
  if (!reasons || reasons.length === 0) {
    return null;
  }

  return (
    <div className="UserNotificationSettings-barnBadges">
      {reasons.includes(BARN_DIFF_REASON.TIER) && (
        <StatusBadge status="warning" text={BARN_DIFF_REASON.TIER} />
      )}
      {reasons.includes(BARN_DIFF_REASON.SETTINGS) && (
        <StatusBadge status="info" text={BARN_DIFF_REASON.SETTINGS} />
      )}
    </div>
  );
}

BarnPolicyReasonBadges.propTypes = {
  reasons: PropTypes.arrayOf(PropTypes.string),
};

function BarnPolicyCard({ barn, settings, effectiveTierName, reasons, canEditBarnPolicies }) {
  const headerText = `You will receive messages for the following events at ${barn.barnName}`;

  return (
    <div className="UserNotificationSettings-barnTier">
      <div className="UserNotificationSettings-barnTierContent">
        <div className="UserNotificationSettings-barnHeader">
          <span className="UserNotificationSettings-barnName">{barn.barnName}</span>
          <PolicyEditAffordance
            canEdit={canEditBarnPolicies}
            editTo={`/barns/${barn.barnId}/settings`}
            editLabel={`Edit ${barn.barnName} notification settings`}
          />
          {effectiveTierName && (
            <div className="UserNotificationSettings-barnTierName">{effectiveTierName}</div>
          )}
          <BarnPolicyReasonBadges reasons={reasons} />
        </div>
        {settings && settings.length > 0 ? (
          <TierSettingsSummaryView settings={settings} headerText={headerText} />
        ) : (
          <div>
            <p>
              Notifications <b>OFF</b>
            </p>
            <p>
              You will not receive <u>any</u> notifications until a policy is assigned.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

BarnPolicyCard.propTypes = {
  barn: PropTypes.shape({
    barnId: PropTypes.string.isRequired,
    barnName: PropTypes.string.isRequired,
  }).isRequired,
  settings: PropTypes.array,
  effectiveTierName: PropTypes.string,
  reasons: PropTypes.arrayOf(PropTypes.string),
  canEditBarnPolicies: PropTypes.bool.isRequired,
};

function OrganizationPolicyCard({ org, can, isExpanded = false }) {
  const barnIds = useMemo(() => org.barnOverrides.map((barn) => barn.barnId), [org.barnOverrides]);

  const {
    loading: orgLoading,
    data: orgData,
    error: orgError,
  } = useQuery(GET_ORG_TIER_SETTINGS_ALL_GQL, {
    variables: { org_id: org.organizationId },
    skip: !org.organizationId || !org.hasOrgAccess,
    fetchPolicy: 'cache-and-network',
    context: { headers: { 'x-hasura-role': 'user' } },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  const {
    loading: barnLoading,
    data: barnData,
    error: barnError,
  } = useQuery(GET_BARN_TIER_SETTINGS_GQL, {
    variables: { barn_ids: barnIds },
    skip: barnIds.length === 0,
    fetchPolicy: 'cache-and-network',
    context: { headers: { 'x-hasura-role': 'user' } },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  const orgSettingsByTier = useMemo(
    () => groupSettingsBy(orgData?.organization_fault_notification_tier_settings_view || [], 'tier_id'),
    [orgData],
  );

  const tierNames = useMemo(() => {
    const names = new Map();
    (orgData?.organization_fault_notification_tier_settings_view || []).forEach((row) => {
      if (row.tier?.name) {
        names.set(row.tier_id, row.tier.name);
      }
    });
    (barnData?.barn_fault_notification_tier_settings_view || []).forEach((row) => {
      if (row.tier?.name) {
        names.set(row.tier_id, row.tier.name);
      }
    });
    return names;
  }, [orgData, barnData]);

  const barnSettingsByKey = useMemo(
    () => groupBarnSettingsByBarnAndTier(barnData?.barn_fault_notification_tier_settings_view || []),
    [barnData],
  );

  const barnsWithPolicy = useMemo(() => {
    const orgAssignedTierId = org.orgAssignedTierId ?? org.orgTier?.id ?? null;

    return org.barnOverrides.map((barn) => {
      const barnAssignedTierId = barn.assignedTierId ?? barn.tier?.id ?? null;
      const effectiveTierId = barnAssignedTierId ?? orgAssignedTierId ?? null;
      const effectiveTierName =
        barn.tier?.name ??
        org.orgTier?.name ??
        (effectiveTierId ? tierNames.get(effectiveTierId) : null);
      const settingsKey = effectiveTierId ? `${barn.barnId}:${effectiveTierId}` : null;
      const barnSettings = settingsKey ? barnSettingsByKey.get(settingsKey) || [] : [];
      const orgSettingsForBarnTier = effectiveTierId ? orgSettingsByTier.get(effectiveTierId) || [] : null;
      const displaySettings =
        barnSettings.length > 0 ? barnSettings : orgSettingsForBarnTier || [];

      const { differs, reasons } = org.hasOrgAccess
        ? getBarnPolicyDifference({
          orgAssignedTierId,
          barnAssignedTierId,
          effectiveTierId,
          orgSettingsForBarnTier,
          barnSettings,
        })
        : { differs: true, reasons: [] };

      return {
        barn,
        effectiveTierId,
        effectiveTierName,
        barnSettings: displaySettings,
        differs,
        reasons,
      };
    });
  }, [org, barnSettingsByKey, orgSettingsByTier, tierNames]);

  const barnsToDisplay = useMemo(() => {
    if (!org.hasOrgAccess) {
      return barnsWithPolicy;
    }
    return barnsWithPolicy.filter((entry) => entry.differs);
  }, [barnsWithPolicy, org.hasOrgAccess]);

  const canEditOrgPolicies = can('org', org.organizationId, Privilege.ORG_GENERAL_ADMINISTRATE);
  const barnSectionTitle = org.hasOrgAccess ? 'Barn-Specific Overrides' : 'Your Barn Policies';
  const differingCount = barnsToDisplay.length;
  const barnCountLabel = org.hasOrgAccess
    ? `(${differingCount} barn${differingCount !== 1 ? 's differ' : ' differs'})`
    : `(${org.barnOverrides.length} barn${org.barnOverrides.length !== 1 ? 's' : ''})`;

  const orgAssignedTierId = org.orgAssignedTierId ?? org.orgTier?.id ?? null;
  const orgTierSettings = orgAssignedTierId ? orgSettingsByTier.get(orgAssignedTierId) || [] : [];
  const isLoading = (org.hasOrgAccess && orgLoading) || (barnIds.length > 0 && barnLoading);
  const queryError = orgError || barnError;

  if (queryError) {
    return (
      <div className="UserNotificationSettings-orgCard">
        <div className="UserNotificationSettings-emptyState">
          Error loading notification policies: {queryError.message}
        </div>
      </div>
    );
  }

  return (
    <div className="UserNotificationSettings-orgCard">
      <CollapsibleSection
        title={
          <div className="UserNotificationSettings-orgHeaderLeft">
            <span className="UserNotificationSettings-orgName">{org.organizationName}</span>
            {org.hasOrgAccess && (
              <>
                <PolicyEditAffordance
                  canEdit={canEditOrgPolicies}
                  editTo={`/orgs/${org.organizationId}/notifications/settings`}
                  editLabel={`Edit ${org.organizationName} notification policies`}
                />
                <span className="UserNotificationSettings-orgTierName">
                  {org.orgTier?.name ??
                    (orgAssignedTierId ? tierNames.get(orgAssignedTierId) : null) ?? (
                      <Link to={`/orgs/${org.organizationId}/users`}>
                        No policy assigned
                      </Link>
                    )}
                </span>
              </>
            )}
          </div>
        }
        rightHeaderContent={
          org.hasOrgAccess && differingCount > 0 ? (
            <span className="UserNotificationSettings-barnCount">{barnCountLabel}</span>
          ) : null
        }
        isExpanded={isExpanded}
      >
        <div className="UserNotificationSettings-orgContent">
          {org.hasOrgAccess && (
            <div className="UserNotificationSettings-orgTier">
              {isLoading ? (
                <LoadingSkeleton style={{ width: '100%', height: '120px' }} />
              ) : orgAssignedTierId ? (
                <TierSettingsSummaryView
                  settings={orgTierSettings}
                  headerText={`You will receive messages for the following events for all sites at ${org.organizationName}`}
                />
              ) : (
                <div>
                  <p>
                    Notifications <b>OFF</b>
                  </p>
                  <p>
                    You will not receive <u>any</u> notifications until a policy is assigned.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="UserNotificationSettings-barnTiers">
            <div className="UserNotificationSettings-barnTiersTitle">{barnSectionTitle}</div>
            {isLoading ? (
              <LoadingSkeleton style={{ width: '100%', height: '120px' }} />
            ) : barnsToDisplay.length > 0 ? (
              barnsToDisplay.map((entry) => {
                const canEditBarnPolicies =
                  can('barn', entry.barn.barnId, 'barn:notfication:policy:self:assign') ||
                  can('barn', entry.barn.barnId, Privilege.BARN_UPDATE);

                return (
                  <BarnPolicyCard
                    key={entry.barn.barnId}
                    barn={entry.barn}
                    settings={entry.barnSettings}
                    effectiveTierName={entry.effectiveTierName}
                    reasons={entry.reasons}
                    canEditBarnPolicies={canEditBarnPolicies}
                  />
                );
              })
            ) : (
              <div className="UserNotificationSettings-barnTier">
                {org.hasOrgAccess ? 'All barns use the organization policy' : 'No barn policies'}
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
}

OrganizationPolicyCard.propTypes = {
  org: PropTypes.shape({
    organizationId: PropTypes.string.isRequired,
    organizationName: PropTypes.string.isRequired,
    orgTier: PropTypes.object,
    orgAssignedTierId: PropTypes.string,
    barnOverrides: PropTypes.arrayOf(
      PropTypes.shape({
        barnId: PropTypes.string.isRequired,
        barnName: PropTypes.string.isRequired,
        tier: PropTypes.object,
        assignedTierId: PropTypes.string,
      }),
    ).isRequired,
    hasOrgAccess: PropTypes.bool.isRequired,
  }).isRequired,
  can: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
};

function UserNotificationSettings({ onChange, smsTestDisabled = false }, ref) {
  // --- Hooks ---
  const { user } = useUser();

  // --- Privileges and Access Hooks ---
  const { can } = useCan();

  // --- State hooks ---
  const [organizationTiers, setOrganizationTiers] = useState([]);

  // --- Refs ---
  const transportSelectorRef = useRef();

  // --- Function definitions ---
  useImperativeHandle(ref, () => ({
    save: async () => {
      if (transportSelectorRef.current?.save) {
        await transportSelectorRef.current.save();
      }
      return Promise.resolve();
    },
  }));

  // --- Apollo/GraphQL hooks ---
  const { loading: tierAssignmentsLoading, data: tierAssignmentsData } = useQuery(GET_USER_TIER_ASSIGNMENTS_GQL, {
    variables: { userId: user?.id },
    context: { headers: { 'x-hasura-role': 'user' } },
    skip: !user?.id,
    fetchPolicy: 'cache-and-network',
  });

  // --- Navigation/side effects ---
  useEffect(() => {
    if (!tierAssignmentsData) return;

    const barnsByOrg = {};
    const barnIdsByOrg = {};
    const orgMetaFromBarns = {};

    const addBarnToOrg = (orgId, orgName, barnEntry) => {
      if (!barnsByOrg[orgId]) {
        barnsByOrg[orgId] = [];
        barnIdsByOrg[orgId] = new Set();
        orgMetaFromBarns[orgId] = { name: orgName };
      }

      if (barnIdsByOrg[orgId].has(barnEntry.barnId)) {
        const existing = barnsByOrg[orgId].find((barn) => barn.barnId === barnEntry.barnId);
        if (existing && !existing.assignedTierId && barnEntry.assignedTierId) {
          existing.tier = barnEntry.tier;
          existing.assignedTierId = barnEntry.assignedTierId;
        }
        return;
      }

      barnIdsByOrg[orgId].add(barnEntry.barnId);
      barnsByOrg[orgId].push(barnEntry);
    };

    if (tierAssignmentsData.user_barn) {
      tierAssignmentsData.user_barn.forEach((userBarn) => {
        const orgId = userBarn.barn.organization.id;
        addBarnToOrg(orgId, userBarn.barn.organization.name, {
          barnId: userBarn.barn.id,
          barnName: userBarn.barn.name,
          tier: userBarn.user_barn_notification_tier_assignment?.tier || null,
          assignedTierId:
            userBarn.user_barn_notification_tier_assignment?.tier_id ??
            userBarn.user_barn_notification_tier_assignment?.tier?.id ??
            null,
        });
      });
    }

    if (tierAssignmentsData.user_barn_tier_assignments) {
      tierAssignmentsData.user_barn_tier_assignments.forEach((tierAssignment) => {
        const orgId = tierAssignment.barn.organization.id;
        addBarnToOrg(orgId, tierAssignment.barn.organization.name, {
          barnId: tierAssignment.barn.id,
          barnName: tierAssignment.barn.name,
          tier: tierAssignment.tier || null,
          assignedTierId: tierAssignment.tier_id ?? tierAssignment.tier?.id ?? null,
        });
      });
    }

    const orgMap = new Map();

    if (tierAssignmentsData.user_organization) {
      tierAssignmentsData.user_organization.forEach((userOrg) => {
        const orgId = userOrg.organization.id;
        orgMap.set(orgId, {
          organizationId: orgId,
          organizationName: userOrg.organization.name,
          organizationPlan: userOrg.organization.selected_notification_plan?.plan || null,
          orgTier: userOrg.user_organization_notification_tier_assignment?.tier || null,
          orgAssignedTierId:
            userOrg.user_organization_notification_tier_assignment?.tier_id ??
            userOrg.user_organization_notification_tier_assignment?.tier?.id ??
            null,
          barnOverrides: barnsByOrg[orgId] || [],
          hasOrgAccess: true,
        });
      });
    }

    Object.keys(barnsByOrg).forEach((orgId) => {
      if (!orgMap.has(orgId)) {
        orgMap.set(orgId, {
          organizationId: orgId,
          organizationName: orgMetaFromBarns[orgId].name,
          organizationPlan: null,
          orgTier: null,
          orgAssignedTierId: null,
          barnOverrides: barnsByOrg[orgId],
          hasOrgAccess: false,
        });
      }
    });

    const orgTiers = Array.from(orgMap.values()).sort((a, b) =>
      a.organizationName.localeCompare(b.organizationName),
    );

    setOrganizationTiers(orgTiers);
  }, [tierAssignmentsData]);

  // --- Render ---
  if (tierAssignmentsLoading) {
    return (
      <div className="UserNotificationSettings">
        <div className="UserNotificationSettings-section">
          <div className="UserNotificationSettings-sectionTitle">
            <LoadingSkeleton className="UserNotificationSettings-titleSkeleton" />
          </div>
          <div className="UserNotificationSettings-description">
            <LoadingSkeleton className="UserNotificationSettings-descriptionSkeleton" />
          </div>

          <div className="UserNotificationSettings-transportSkeleton">
            <LoadingSkeleton className="UserNotificationSettings-transportLabel" />
            <div className="UserNotificationSettings-transportMethods">
              <LoadingSkeleton className="UserNotificationSettings-transportMethod" />
              <LoadingSkeleton className="UserNotificationSettings-transportMethod" />
              <LoadingSkeleton className="UserNotificationSettings-transportMethod" />
            </div>
          </div>
        </div>

        <div className="UserNotificationSettings-section">
          <div className="UserNotificationSettings-sectionTitle">
            <LoadingSkeleton className="UserNotificationSettings-titleSkeleton" />
          </div>
          <div className="UserNotificationSettings-description">
            <LoadingSkeleton className="UserNotificationSettings-descriptionSkeleton" />
          </div>

          <div className="UserNotificationSettings-tierSection">
            <div className="UserNotificationSettings-orgCard">
              <div className="UserNotificationSettings-orgHeader">
                <div className="UserNotificationSettings-orgHeaderLeft">
                  <LoadingSkeleton className="UserNotificationSettings-orgNameSkeleton" />
                  <LoadingSkeleton className="UserNotificationSettings-orgTierSkeleton" />
                </div>
                <LoadingSkeleton className="UserNotificationSettings-barnCountSkeleton" />
              </div>

              <div className="UserNotificationSettings-orgContent">
                <div className="UserNotificationSettings-orgTier">
                  <LoadingSkeleton className="UserNotificationSettings-tierSelectorSkeleton" />
                </div>

                <div className="UserNotificationSettings-barnTiers">
                  <LoadingSkeleton className="UserNotificationSettings-barnTiersTitleSkeleton" />
                  <div className="UserNotificationSettings-barnTier">
                    <LoadingSkeleton className="UserNotificationSettings-barnNameSkeleton" />
                    <LoadingSkeleton className="UserNotificationSettings-barnTierSelectorSkeleton" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="UserNotificationSettings">
      <div className="UserNotificationSettings-section">
        <Heading className="UserNotificationSettings-sectionTitle">Notification Preferences</Heading>
        <div className="UserNotificationSettings-description">
          Choose how you want to receive notifications. Your tier determines which alerts you&apos;ll receive.
        </div>

        <TransportMethodSelector
          ref={transportSelectorRef}
          onChange={onChange}
          showDetails={true}
          label="Notification Methods"
          smsTestDisabled={smsTestDisabled}
        />
        <MessageHistoryLink filters={{ userId: user?.id }} />
      </div>

      <div className="UserNotificationSettings-section">
        <Heading className="UserNotificationSettings-sectionTitle">Notification Policies</Heading>
        <div className="UserNotificationSettings-description">
          Your assigned policy determines which alerts you receive. Barns listed below differ from your
          organization-wide policy.
        </div>

        <div className="UserNotificationSettings-tierSection">
          {organizationTiers.length === 0 ? (
            <div className="UserNotificationSettings-emptyState">
              You have not been assigned to any notification policies yet. Contact your organization administrator.
            </div>
          ) : (
            organizationTiers.map((org) => (
              <OrganizationPolicyCard
                key={org.organizationId}
                org={org}
                can={can}
                isExpanded={organizationTiers.length === 1}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

UserNotificationSettings.displayName = 'UserNotificationSettings';

UserNotificationSettings.propTypes = {
  onChange: PropTypes.func,
  smsTestDisabled: PropTypes.bool,
};

export default forwardRef(UserNotificationSettings);
